// Considered: a bespoke condition-row list — rejected because
// `everySurfaceMountsTheSameTree` retires exactly that; every host authors
// conditions through `UnifiedFilterTreeMolecule`, widened here rather than
// reimplemented. See spec Reuse Audit.
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import UnifiedFilterTreeMolecule from '../../UnifiedFilterTree/UnifiedFilterTreeMolecule';
import AdvancedFilterEditorOrganism from '../../../organisms/AdvancedFilterEditor/AdvancedFilterEditorOrganism';
import { withStableFilterGroupIds } from '../../../../utils/filterGroupIds';
import { seedLeavesIntoGroups, topLevelRowToLeaf } from '../../../../utils/filterGroupSeeding';
import { simpleGroupToUnified, unifiedGroupToSimple } from '../../../../utils/contextFlowSerialization';
import { nextFilterId, withStableIds } from '../../../../utils/ids';
import { RollupSetModel, setTree } from '../rollupSetModel';
import type { PropertyOption, PropertyType } from '../../PropertyFilter/PropertyFilterMolecule';
import type { ArcTemplateOption } from '../../../../hooks/useArcTemplates';
import type { RelationshipOption } from '../../RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../../PerspectiveFilter/PerspectiveFilterMolecule';
import type { OrgAvailableTypeSchema } from 'types/schema';
import type { FilterGroup, FilterGroupLeaf } from 'types/filters';
import type { FilterState } from '../../../../types/filterState';
import './NarrowedToBand.scss';

export interface NarrowedToBandProps {
  value: RollupSetModel;
  onChange: (next: RollupSetModel) => void;
  availableProperties: PropertyOption[];
  excludePropertyTypes?: PropertyType[];
  arcTemplates?: ArcTemplateOption[];
  availableRelationships?: RelationshipOption[];
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  availablePerspectives?: PerspectiveOption[];
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  renderCatalog?: (opts: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => React.ReactNode;
  scopeTypeLabels?: string[];
  disabled?: boolean;
  className?: string;
}

/** Folds an advanced-editor `FilterState` result back onto one canonical root — same rule as `RelatedSpheresEditorMolecule.rootGroup`/`SegmentPanelMolecule.rootGroup`. */
function filterStateToTree(filterState: FilterState): FilterGroup {
  const propertyFilters = withStableIds(filterState.propertyFilters ?? []);
  const groups = filterState.filterGroups ?? [];
  const seededGroups = seedLeavesIntoGroups(groups, propertyFilters.map(topLevelRowToLeaf));
  const stableGroups = withStableFilterGroupIds(seededGroups);
  if (stableGroups.length === 1) return simpleGroupToUnified(stableGroups[0]);
  return {
    id: nextFilterId(),
    operator: 'and',
    expressions: [],
    groups: stableGroups.map(simpleGroupToUnified),
  };
}

const NarrowedToBand: React.FC<NarrowedToBandProps> = ({
  value,
  onChange,
  availableProperties,
  excludePropertyTypes,
  arcTemplates,
  availableRelationships,
  familyTypeSchemas,
  availablePerspectives,
  perspectiveSearchProps,
  renderCatalog,
  scopeTypeLabels,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const handleTreeChange = useCallback(
    (next: FilterGroup) => onChange(setTree(value, next)),
    [value, onChange]
  );

  // Escalation carries the current tree — never a blank builder (spec Band 02).
  const filterStateForAdvanced = useMemo<FilterState>(
    () => ({ propertyFilters: [], perspectiveFilters: [], filterGroups: [unifiedGroupToSimple(value.tree)] }),
    [value.tree]
  );

  const handleAdvancedApply = useCallback(
    (next: FilterState) => {
      onChange(setTree(value, filterStateToTree(next)));
      setAdvancedOpen(false);
    },
    [value, onChange]
  );

  if (advancedOpen) {
    return (
      <div className={`narrowed-to-band ${className}`} data-testid="narrowed-to-band">
        <AdvancedFilterEditorOrganism
          filter={filterStateForAdvanced}
          onApply={handleAdvancedApply}
          onBack={() => setAdvancedOpen(false)}
          label={t('statusFactors.set.narrowedTo.label', 'Narrowed to')}
          availableProperties={availableProperties}
          availablePerspectives={availablePerspectives ?? []}
          availableRelationships={availableRelationships ?? []}
          arcTemplates={arcTemplates ?? []}
          familyTypeSchemas={familyTypeSchemas ?? []}
          perspectiveSearchProps={perspectiveSearchProps}
          renderCatalog={renderCatalog}
          readOnly={disabled}
        />
      </div>
    );
  }

  return (
    <div className={`narrowed-to-band ${className}`} data-testid="narrowed-to-band">
      <div className="narrowed-to-band__header">
        <span className="narrowed-to-band__label">{t('statusFactors.set.narrowedTo.label', 'Narrowed to')}</span>
        <span className="narrowed-to-band__caption">
          {t('statusFactors.set.narrowedTo.caption', 'conditions the candidates must also match')}
        </span>
        <span className="narrowed-to-band__spacer" />
        {!disabled && (
          <button
            type="button"
            className="narrowed-to-band__advanced-btn"
            onClick={() => setAdvancedOpen(true)}
            data-testid="narrowed-to-advanced"
          >
            <i className="pi pi-sliders-h" aria-hidden="true" />
            {t('statusFactors.set.narrowedTo.advanced', 'Advanced')}
          </button>
        )}
      </div>

      <UnifiedFilterTreeMolecule
        value={value.tree}
        onChange={handleTreeChange}
        availableProperties={availableProperties}
        excludePropertyTypes={excludePropertyTypes}
        arcTemplates={arcTemplates}
        availableRelationships={availableRelationships}
        familyTypeSchemas={familyTypeSchemas}
        availablePerspectives={availablePerspectives}
        perspectiveSearchProps={perspectiveSearchProps}
        renderCatalog={renderCatalog}
        scopeTypeLabels={scopeTypeLabels}
        readOnly={disabled}
        readsAs={false}
        subject={t('statusFactors.set.narrowedTo.subject', 'Spheres')}
        dense
        alwaysShowBar
        emptyNote={t(
          'statusFactors.set.narrowedTo.emptyNote',
          'No conditions yet — every sphere the relation returns is in the set. Add one to narrow it.'
        )}
        datumScope="rollup-set-narrowed-to-filter-tree"
        container="ui-rollup-set-narrowed-to"
        className="narrowed-to-band__filter-tree"
      />
    </div>
  );
};

NarrowedToBand.displayName = 'NarrowedToBand';

export default NarrowedToBand;
